import Box from '@mui/material/Box';
import { useEffect, useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EmployeeRequestRecordSchema,
  EmployeeRequestTypeSchema,
  REQUEST_STATUSES,
  type EmployeeDto,
  type EmployeeRequestRecordDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';
import { ApiError, getData, getPage, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { filterQuery } from '../../shared/export/filter-query';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { ApprovalPanel } from '../../shared/workflow/ApprovalPanel';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { EmployeePicker, MasterSelect, useActiveMasters } from '../finance/lookups';
import { CreatedNotice, messageOf, NoEmployeeLink } from './leave';
import { useFilterEmployee, useUrlFilters } from './url-filters';
import { useActionFeedback } from '../happiness/ActionFeedback';
import { SearchFilterBar } from '../../shared/ui/filters';

/**
 * Employee requests (FRD-HRP-034, HRP-035, HRP-037): HR requests of a configured type, submitted
 * through self-service or by HR, routed for approval, with supporting documents.
 */

const REQUESTS = '/api/v1/hr/employee-requests';
const requestPath = (id: string) => `/hr/requests/${encodeURIComponent(id)}`;

function useRequestTypes() {
  return useQuery({
    queryKey: ['hr', 'employee-requests', 'types'],
    queryFn: () => getData('/api/v1/hr/employee-request-types', z.array(EmployeeRequestTypeSchema)),
  });
}

/** My requests (`/hr/my-requests`): the signed-in employee's own requests (FRD-HRP-034). */
export function MyRequestsPage() {
  const session = useSession();
  const navigate = useNavigate();
  const canCreate = hasCapability(session.data, 'hr.employee_request:create');
  const [creating, setCreating] = useState(false);
  // The first page of the list below (the same query, so it is read once): without an employee
  // link there is no "mine", and the API says so with a 404, as My leave does.
  const firstPage = useRequestPage(MINE, 0, 25);
  const unlinked = firstPage.error instanceof ApiError && firstPage.error.status === 404;
  return (
    <Stack spacing={3}>
      <PageHeader
        title="My requests"
        description="Your HR requests and where each one is."
        actions={
          canCreate && !unlinked ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              New request
            </Button>
          ) : undefined
        }
      />
      {unlinked ? (
        <NoEmployeeLink />
      ) : (
        <Section>
          <RequestList filters={MINE} showEmployee={false} emptyTitle="You have no requests" />
        </Section>
      )}
      {creating && (
        <RequestDialog
          employee={null}
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`${requestPath(saved.id)}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

const REQUEST_FILTER_KEYS = ['employee', 'department', 'type', 'status'] as const;

/** Employee requests (`/hr/requests`): every request the session may see, filtered on the server. */
export function EmployeeRequestsPage() {
  const session = useSession();
  const navigate = useNavigate();
  const canCreate = hasCapability(session.data, 'hr.employee_request:create');
  const canPickEmployees = hasCapability(session.data, 'hr.employee:view');
  const canSeeDepartments = hasCapability(session.data, 'organization.department:view');
  const departments = useActiveMasters('/api/v1/organization/departments', canSeeDepartments);
  const types = useRequestTypes();
  // Filters live in the URL, so a filtered list survives a refresh and can be shared.
  const [url, setUrl] = useUrlFilters(REQUEST_FILTER_KEYS);
  const employee = useFilterEmployee(url.employee, canPickEmployees);
  const { department: departmentId, type, status } = url;
  const setEmployee = (value: EmployeeDto | null) => {
    setUrl('employee', value?.id ?? '');
  };
  const setDepartmentId = (value: string) => {
    setUrl('department', value);
  };
  const setType = (value: string) => {
    setUrl('type', value);
  };
  const setStatus = (value: string) => {
    setUrl('status', value);
  };
  const [creating, setCreating] = useState(false);
  const filters = {
    employee_id: url.employee,
    department_id: departmentId,
    request_type: type,
    status,
  };
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Employee requests"
        description="HR requests across the employees you may see."
        actions={
          <>
            <ExportButton
              recordType="hr.employee_request"
              href={exportHref(REQUESTS, filterQuery(filters, '-submitted_at'))}
            />
            {canCreate && canPickEmployees && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New request
              </Button>
            )}
          </>
        }
      />
      <Box role="search" aria-label="Filter employee requests">
        <SearchFilterBar>
          {canPickEmployees && (
            <Stack sx={{ minWidth: 240 }}>
              <EmployeePicker value={employee} onChange={setEmployee} />
            </Stack>
          )}
          {canSeeDepartments && (
            <MasterSelect
              label="Department"
              value={departmentId}
              onChange={setDepartmentId}
              options={departments.data?.items ?? []}
            />
          )}
          <TextField
            select
            size="small"
            label="Request type"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Any</MenuItem>
            {(types.data ?? []).map((t) => (
              <MenuItem key={t.valueCode} value={t.valueCode}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Any</MenuItem>
            {REQUEST_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </SearchFilterBar>
      </Box>
      <Section>
        <RequestList
          key={JSON.stringify(filters)}
          filters={filters}
          showEmployee
          emptyTitle="No requests found"
        />
      </Section>
      {creating && (
        <RequestDialog
          employee={undefined}
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`${requestPath(saved.id)}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

const MINE: Readonly<Record<string, string>> = { mine: 'true' };

/** One page of requests, filtered on the server. */
function useRequestPage(filters: Readonly<Record<string, string>>, page: number, pageSize: number) {
  const params = new URLSearchParams({
    page: String(page + 1),
    pageSize: String(pageSize),
    sort: '-submitted_at',
  });
  for (const [key, value] of Object.entries(filters)) {
    if (value !== '') params.set(`filter[${key}]`, value);
  }
  return useQuery({
    queryKey: ['hr', 'employee-requests', 'list', params.toString()],
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 3,
    queryFn: () => getPage(`${REQUESTS}?${params.toString()}`, EmployeeRequestRecordSchema),
  });
}

function RequestList({
  filters,
  showEmployee,
  emptyTitle,
}: {
  filters: Readonly<Record<string, string>>;
  showEmployee: boolean;
  emptyTitle: string;
}) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const requests = useRequestPage(filters, page, pageSize);
  return (
    <>
      {requests.isPending && <LoadingState label="Loading requests" />}
      {requests.isError && <ErrorState message={requests.error.message} />}
      {requests.data?.items.length === 0 && <EmptyState title={emptyTitle} />}
      {requests.data !== undefined && requests.data.items.length > 0 && (
        <>
          <Table size="small" aria-label="Employee requests">
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                {showEmployee && <TableCell>Employee</TableCell>}
                <TableCell>Type</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.data.items.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Link component={RouterLink} to={requestPath(r.id)} sx={{ fontWeight: 600 }}>
                      {r.code}
                    </Link>
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      <PersonName name={r.employeeName} employeeId={r.employeeId}>
                        {r.employeeCode} — {r.employeeName}
                      </PersonName>
                    </TableCell>
                  )}
                  <TableCell>{r.requestTypeLabel}</TableCell>
                  <TableCell>{r.subject}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(r.submittedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={r.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={requests.data.total}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(_, next) => {
              setPage(next);
            }}
            onRowsPerPageChange={(e) => {
              setPageSize(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </>
  );
}

/** Submit a request, or edit a returned / rejected one and resubmit it. */
function RequestDialog({
  employee,
  editing,
  onClose,
}: {
  /** Null: the caller's own record. Undefined: choose one with the picker. */
  employee: EmployeeDto | null | undefined;
  editing?: EmployeeRequestRecordDto;
  onClose: (saved?: EmployeeRequestRecordDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const types = useRequestTypes();
  const [chosen, setChosen] = useState<EmployeeDto | null>(null);
  const [type, setType] = useState(editing?.requestTypeCode ?? '');
  const [subject, setSubject] = useState(editing?.subject ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const fields = {
    requestTypeCode: type,
    subject: subject.trim(),
    ...(description.trim() === '' ? {} : { description: description.trim() }),
  };
  const save = useMutation({
    mutationFn: () =>
      editing === undefined
        ? postJson(
            REQUESTS,
            employee === undefined && chosen !== null
              ? { employeeId: chosen.id, ...fields }
              : fields,
            EmployeeRequestRecordSchema,
            session.data?.csrfToken ?? '',
          )
        : postJson(
            `${REQUESTS}/${editing.id}/resubmit`,
            fields,
            EmployeeRequestRecordSchema,
            session.data?.csrfToken ?? '',
            { expectedVersion: editing.version },
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employee-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['workflow'] });
      onClose(saved);
    },
  });
  // Inactive types stay listed only for a request that already has one.
  const options = (types.data ?? []).filter(
    (t) => t.active || t.valueCode === editing?.requestTypeCode,
  );
  return (
    <FormDialog
      open
      title={editing === undefined ? 'New request' : `Edit and resubmit ${editing.code}`}
      submitLabel={editing === undefined ? 'Submit request' : 'Resubmit'}
      canSubmit={
        type !== '' &&
        subject.trim() !== '' &&
        (editing !== undefined || employee !== undefined || chosen !== null)
      }
      submitting={save.isPending}
      error={save.isError ? messageOf(save.error) : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {employee === undefined && editing === undefined && (
        <EmployeePicker required value={chosen} onChange={setChosen} />
      )}
      {types.isError && <ErrorState message={types.error.message} />}
      <TextField
        select
        required
        label="Request type"
        value={type}
        onChange={(e) => {
          setType(e.target.value);
        }}
        helperText={
          options.length === 0 && !types.isPending
            ? 'No request type is configured yet (Administration › List values)'
            : undefined
        }
      >
        {options.map((t) => (
          <MenuItem key={t.valueCode} value={t.valueCode}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        required
        label="Subject"
        value={subject}
        onChange={(e) => {
          setSubject(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />
      <TextField
        label="Description"
        multiline
        minRows={3}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
        slotProps={{ htmlInput: { maxLength: 4000 } }}
      />
    </FormDialog>
  );
}

/** One employee request (`/hr/requests/:id`). */
export function EmployeeRequestDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const session = useSession();
  const detail = useQuery({
    queryKey: ['hr', 'employee-requests', 'one', id],
    queryFn: () => getData(`${REQUESTS}/${id}`, EmployeeRequestRecordSchema),
  });
  const own = detail.data !== undefined && detail.data.requestedBy === session.data?.userId;
  const back = (
    <Button
      component={RouterLink}
      to={own ? '/hr/my-requests' : '/hr/requests'}
      startIcon={<ArrowBackIcon />}
    >
      {own ? 'My requests' : 'Employee requests'}
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the request" />;
  if (detail.isError) {
    return (
      <RecordFailure title="Employee request" noun="request" error={detail.error} back={back} />
    );
  }
  return (
    <RequestRecord request={detail.data} back={back} created={params.get('created') === '1'} />
  );
}

function RequestRecord({
  request,
  back,
  created,
}: {
  request: EmployeeRequestRecordDto;
  back: ReactNode;
  created: boolean;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  // Just submitted: offer to rate how it went (FRD-CORE-062), once a day.
  const feedback = useActionFeedback();
  const { offer } = feedback;
  useEffect(() => {
    if (created) offer('employee_request');
  }, [created, offer]);
  const isRequester = session.data?.userId === request.requestedBy;
  const canChange = hasCapability(session.data, 'hr.employee_request:change');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState(false);
  const cancel = useMutation({
    mutationFn: () =>
      postJson(
        `${REQUESTS}/${request.id}/cancel`,
        {},
        EmployeeRequestRecordSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: request.version },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'employee-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['workflow'] });
      setCancelling(false);
    },
  });
  const open = request.status === 'submitted' || request.status === 'returned';
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Request ${request.code}`}
        description={`${request.employeeName} · ${request.requestTypeLabel}`}
        actions={
          <>
            {back}
            {request.status === 'submitted' && (isRequester || canChange) && (
              <Button
                variant="outlined"
                color="warning"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCancelling(true);
                }}
              >
                Withdraw
              </Button>
            )}
            {(request.status === 'returned' || request.status === 'rejected') && isRequester && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setEditing(true);
                }}
              >
                Edit and resubmit
              </Button>
            )}
          </>
        }
      />
      {created && <CreatedNotice code={request.code} />}
      {feedback.dialog}
      <Section title="Request">
        <Facts
          label="Employee request details"
          facts={[
            ['Status', <StatusChip key="s" status={request.status} />],
            ['Number', request.code],
            [
              'Employee',
              <PersonName key="e" name={request.employeeName} employeeId={request.employeeId}>
                {canSeeEmployees ? (
                  <RecordLink to={`/hr/employees/${request.employeeId}`}>
                    {request.employeeCode} — {request.employeeName}
                  </RecordLink>
                ) : (
                  `${request.employeeCode} — ${request.employeeName}`
                )}
              </PersonName>,
            ],
            ['Type', request.requestTypeLabel],
            ['Subject', request.subject],
            ['Description', request.description ?? '—'],
            ['Submitted', new Date(request.submittedAt).toLocaleString()],
            [
              // An employee request is cancelled only by withdrawing it from approval.
              request.status === 'cancelled' ? 'Withdrawn' : 'Decided',
              request.decidedAt === null ? '—' : new Date(request.decidedAt).toLocaleString(),
            ],
          ]}
        />
      </Section>
      <ApprovalPanel
        recordType="hr.employee_request"
        recordId={request.id}
        onDecided={() => {
          void queryClient.invalidateQueries({ queryKey: ['hr', 'employee-requests'] });
        }}
      />
      <DocumentsPanel
        recordType="hr.employee_request"
        recordId={request.id}
        canAttach={open && (isRequester || canChange)}
        title="Supporting documents"
      />
      <RecordHistory recordType="hr.employee_request" recordId={request.id} />
      <ConfirmDialog
        open={cancelling}
        title={`Withdraw request ${request.code}?`}
        message="The request is withdrawn from approval. This cannot be undone."
        confirmLabel="Withdraw"
        tone="warning"
        busy={cancel.isPending}
        error={cancel.isError ? cancel.error.message : null}
        onCancel={() => {
          setCancelling(false);
        }}
        onConfirm={() => {
          cancel.mutate();
        }}
      />
      {editing && (
        <RequestDialog
          employee={null}
          editing={request}
          onClose={() => {
            setEditing(false);
          }}
        />
      )}
    </Stack>
  );
}
